import React, { useEffect, useMemo, useState } from 'react';
import { ProgressBar, Spinner } from 'react-bootstrap';
import {
    IconCheck,
    IconAlertTriangle,
    IconX,
    IconChevronDown,
    IconChevronUp,
    IconCloudUpload,
} from '@tabler/icons-react';
import { useRateImportStatus } from '../../../services/rate-import/hooks/useRateImportStatus';

interface RateImportProgressProps {
    /** Import id yang sedang diproses di background. */
    importId: string;
    /** Dipanggil sekali saat import selesai sukses (untuk refetch data). */
    onDone?: () => void;
    /** Dipanggil saat widget di-dismiss (bersihkan state di parent). */
    onDismiss: () => void;
    /** Delay auto-dismiss setelah sukses (ms). Default 5000. */
    autoDismissMs?: number;
}

/**
 * Floating progress widget ala Google Drive (pojok kanan bawah).
 * Melakukan polling status import di background walau modal sudah ditutup.
 */
const RateImportProgress: React.FC<RateImportProgressProps> = ({
    importId,
    onDone,
    onDismiss,
    autoDismissMs = 5000,
}) => {
    const [minimized, setMinimized] = useState(false);
    const [doneFired, setDoneFired] = useState(false);

    const { data: statusResponse } = useRateImportStatus(importId, {
        enabled: !!importId,
    });

    const status = statusResponse?.data;

    const progressPct = useMemo(() => {
        if (!status || status.total <= 0) return 0;
        return Math.min(100, Math.round((status.processed / status.total) * 100));
    }, [status]);

    const isCompleted = status?.status === 'completed';
    const isFailed = status?.status === 'failed';

    // Panggil onDone sekali saat sukses.
    useEffect(() => {
        if (isCompleted && !doneFired) {
            setDoneFired(true);
            onDone?.();
        }
    }, [isCompleted, doneFired, onDone]);

    // Auto-dismiss beberapa detik setelah sukses.
    useEffect(() => {
        if (!isCompleted) return;
        const timer = setTimeout(() => {
            onDismiss();
        }, autoDismissMs);
        return () => clearTimeout(timer);
    }, [isCompleted, autoDismissMs, onDismiss]);

    const headerText = isCompleted
        ? 'Rates imported'
        : isFailed
        ? 'Import failed'
        : 'Importing rates…';

    return (
        <div
            style={{
                position: 'fixed',
                bottom: 20,
                right: 20,
                width: 320,
                maxWidth: 'calc(100vw - 40px)',
                zIndex: 1080,
                boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
                borderRadius: 8,
                background: 'var(--bs-body-bg, #fff)',
                border: '1px solid var(--bs-border-color, #dee2e6)',
                overflow: 'hidden',
            }}
        >
            {/* Header */}
            <div
                className="d-flex align-items-center gap-2 px-3 py-2"
                style={{
                    background: isFailed
                        ? 'var(--bs-danger-bg-subtle, #f8d7da)'
                        : isCompleted
                        ? 'var(--bs-success-bg-subtle, #d1e7dd)'
                        : 'var(--bs-light, #f8f9fa)',
                    borderBottom: '1px solid var(--bs-border-color, #dee2e6)',
                }}
            >
                <span className="flex-shrink-0 d-flex align-items-center">
                    {isCompleted ? (
                        <IconCheck size={18} className="text-success" />
                    ) : isFailed ? (
                        <IconAlertTriangle size={18} className="text-danger" />
                    ) : (
                        <IconCloudUpload size={18} className="text-primary" />
                    )}
                </span>
                <span className="fw-semibold small flex-grow-1 text-truncate">
                    {headerText}
                </span>
                <button
                    type="button"
                    className="btn btn-sm p-0 text-muted border-0 bg-transparent"
                    onClick={() => setMinimized((m) => !m)}
                    aria-label={minimized ? 'Expand' : 'Minimize'}
                    title={minimized ? 'Expand' : 'Minimize'}
                >
                    {minimized ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />}
                </button>
                <button
                    type="button"
                    className="btn btn-sm p-0 text-muted border-0 bg-transparent"
                    onClick={onDismiss}
                    aria-label="Dismiss"
                    title="Dismiss"
                >
                    <IconX size={16} />
                </button>
            </div>

            {/* Body */}
            {!minimized && (
                <div className="px-3 py-2">
                    {isFailed ? (
                        <div className="small text-danger">
                            {status?.error_message ||
                                'An error occurred while processing the import.'}
                        </div>
                    ) : isCompleted ? (
                        <div className="small text-muted">
                            {status?.processed ?? 0} processed
                            {(status?.failed ?? 0) > 0 && (
                                <span className="text-danger">
                                    {' '}
                                    &middot; {status?.failed} failed
                                </span>
                            )}
                        </div>
                    ) : (
                        <>
                            <div className="d-flex align-items-center gap-2 mb-1">
                                <Spinner
                                    as="span"
                                    animation="border"
                                    size="sm"
                                    variant="primary"
                                />
                                <span className="small text-muted">
                                    Importing rates… {status?.processed ?? 0}/
                                    {status?.total ?? 0}
                                </span>
                            </div>
                            <ProgressBar
                                now={progressPct}
                                animated
                                striped
                                style={{ height: 6 }}
                            />
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default RateImportProgress;
